import React from 'react';

import { IS_LOCAL, REQ_SOURCE, isDefined } from '@nfctron/nexus-core';
import { NfctronTextLogo, UnitedTicketsLogo } from '@nfctron/nexus-react-svg';

import { type OrgIntegratorHost, orgIntegratorHosts } from '@root/config/misc.config';

export interface OrgIntegratorConfig {
	isMain: boolean;
	orgIntegratorId: string;
	name: string;
	backUrl: URL;
	logoNode: React.ReactNode;
	termsUrl?: URL;
	reqSource: REQ_SOURCE.WEB | REQ_SOURCE.WEB_UNITED_TICKETS | REQ_SOURCE.WEB_DINO_LIVE;
	matcher: (locationParams: { hostname: string; pathname: string; origin: string }) => boolean;
	getEventUrl?: (input: { eventSlug: string; organizerSlug: string; locale: string | undefined }) => URL;
	getEventGroupUrl?: (input: { eventGroupSlug: string; locale: string | undefined }) => URL;
	getOrganizerUrl?: (input: { organizerSlug: string; locale: string | undefined }) => URL;
	dnsConfig: {
		hostname: string;
		organizerSlugs: Array<string | RegExp>;
		eventGroupSlugs: Array<string | RegExp>;
		customRobotsRules?: string[];
	} | null;
}

export interface OrgIntegratorConfigReturn extends OrgIntegratorConfig {
	getEventUrl: (input: { eventSlug: string; organizerSlug: string; locale: string | undefined }) => URL;
	getEventGroupUrl: (input: { eventGroupSlug: string; locale: string | undefined }) => URL;
	getOrganizerUrl: (input: { organizerSlug: string; locale: string | undefined }) => URL;
}

const DEFAULT_ORG_INTEGRATOR = {
	orgIntegratorId: 'nfctron',
	name: 'NFCtron',
	isMain: true,
	backUrl: new URL('https://www.nfctron.com'),
	logoNode: <NfctronTextLogo className="text-lucky-point-500 h-6 w-[100px]" />,
	matcher: (url) => url.hostname === 'nfctron.com',
	reqSource: REQ_SOURCE.WEB,
	dnsConfig: null,
} as const satisfies OrgIntegratorConfig;

export const ORG_INTEGRATORS_DNS_HOSTS = {
	unitedTickets: {
		isMain: false,
		orgIntegratorId: 'unitedTickets',
		name: 'United Tickets',
		backUrl: new URL('https://www.united-tickets.cz'),
		logoNode: <UnitedTicketsLogo className="h-10 w-20" />,
		matcher(url) {
			return url.hostname === orgIntegratorHosts.unitedTickets[0].value;
		},
		reqSource: REQ_SOURCE.WEB_UNITED_TICKETS,
		dnsConfig: {
			hostname: orgIntegratorHosts.unitedTickets[0].value,
			organizerSlugs: [/^universal-music(?!\/mirai-summer-fest-2025).*$/],
			/** TODO: this could be removed as only organizerSlug would be sufficient (but needs to update eventGroupSlugs API) */
			eventGroupSlugs: ['vesna-muzika-slavica-2025', 'poetika-2025', 'gaia-mesiah-elements-tour', 'dorian-bmw-backseat-tour'],
			customRobotsRules: [
				/** disallow all organizers but universal-music */
				'Disallow: /organizer/*',
				'Allow: /organizer/universal-music',
				/** disallow all mirai events */
				'Disallow: /event/universal-music/mirai-summer-fest-2025',
			],
		},
	},
	dinoLive: {
		isMain: false,
		orgIntegratorId: 'dinoLive',
		name: 'DinoLive',
		backUrl: new URL('https://www.dinolive.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/2a08a982-99df-4275-a31d-9ba6743cd083.svg" className="h-10 w-20" alt="DINOLIVE" />,
		matcher(url) {
			return url.hostname === orgIntegratorHosts.dinoLive[0].value;
		},
		reqSource: REQ_SOURCE.WEB_DINO_LIVE,
		dnsConfig: {
			hostname: orgIntegratorHosts.dinoLive[0].value,
			organizerSlugs: ['sidetoside'],
			eventGroupSlugs: [],
		},
	},
	lovestreamTickets: {
		isMain: false,
		orgIntegratorId: 'lovestreamTickets',
		name: 'Lovestream Tickets',
		backUrl: new URL('https://www.lovestream.sk'),
		logoNode: <img src="https://images.nfctron.com/organizers/2a08a982-99df-4275-a31d-9ba6743cd083.svg" className="h-10 w-20" alt="Lovestream" />,
		matcher(url) {
			return url.hostname === orgIntegratorHosts.lovestreamTickets[0].value;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.lovestreamTickets[0].value,
			organizerSlugs: ['silvi-production'],
			eventGroupSlugs: [],
		},
	},
	lovestreamVstupenky: {
		isMain: false,
		orgIntegratorId: 'lovestreamVstupenky',
		name: 'Lovestream Vstupenky',
		backUrl: new URL('https://www.lovestream.sk'),
		logoNode: <img src="https://images.nfctron.com/organizers/2a08a982-99df-4275-a31d-9ba6743cd083.svg" className="h-10 w-20" alt="Lovestream" />,
		matcher(url) {
			return url.hostname === orgIntegratorHosts.lovestreamVstupenky[0].value;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.lovestreamVstupenky[0].value,
			organizerSlugs: ['silvi-production'],
			eventGroupSlugs: [],
		},
	},
	lovestreamTicketsEvents: {
		isMain: false,
		orgIntegratorId: 'lovestreamTicketsEvents',
		name: 'Lovestream Tickets Events',
		backUrl: new URL('https://www.lovestream.sk'),
		logoNode: (
			<img src="https://images.nfctron.com/organizers/2a08a982-99df-4275-a31d-9ba6743cd083.svg" className="h-10 w-20" alt="Lovestream Events" />
		),
		matcher(url) {
			return url.hostname === orgIntegratorHosts.lovestreamTicketsEvents[0].value;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.lovestreamTicketsEvents[0].value,
			organizerSlugs: ['silvi-production'],
			eventGroupSlugs: [],
		},
	},
	miraiFest: {
		isMain: false,
		orgIntegratorId: 'mirai-summer-fest-2025',
		name: 'Mirai Summer Fest',
		backUrl: new URL('https://miraifest.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/7887739f-a786-4232-9df8-cfbdb628793b.png" className="h-10" alt="Mirai Summer Fest" />,
		termsUrl: new URL('https://vstupenky.miraifest.cz/obchodni-podminky'),
		getEventUrl(input) {
			return new URL(`https://vstupenky.miraifest.cz/koncert/${input.eventSlug}`);
		},
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.miraiFest[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('mirai-summer-fest-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB_UNITED_TICKETS,
		dnsConfig: {
			hostname: orgIntegratorHosts.miraiFest[0].value,
			organizerSlugs: [/universal-music\/mirai-summer-fest-2025-.*$/],
			eventGroupSlugs: ['mirai-summer-fest-2025'],
			customRobotsRules: [
				/** disallow all organizers */
				'Disallow: /organizer/*',
				/** disallow all events but mirai */
				'Disallow: /event/*',
				'Allow: /event/universal-music/mirai-summer-fest-2025',
				/** disallow all tours but mirai */
				'Disallow: /tour/*',
				'Allow: /tour/mirai-summer-fest-2025',
			],
		},
	},
	chinaski: {
		orgIntegratorId: 'chinaski-2025',
		name: 'Chinaski',
		isMain: false,
		backUrl: new URL('https://chinaski.cz'),
		termsUrl: new URL('https://vstupenky.chinaski.cz/obchodni-podminky'),
		logoNode: <img src="https://images.nfctron.com/organizers/d83648ea-d9c0-4a4e-b85f-fa6b6851d402.svg" className="h-10" alt="Chinaski" />,
		getEventUrl(input) {
			return new URL(`https://vstupenky.chinaski.cz/koncert/${input.eventSlug}`);
		},
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.chinaski[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('chinaski')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.chinaski[0].value,
			organizerSlugs: ['matchfire-sro'],
			eventGroupSlugs: ['chinaski-2025'],
		},
	},
	marekZtraceny: {
		orgIntegratorId: 'marek-ztraceny-je-zpatky-tour-2025',
		name: 'Marek Ztracený',
		isMain: false,
		backUrl: new URL('https://marekztraceny.cz'),
		termsUrl: new URL('https://vstupenky.marekztraceny.cz/obchodni-podminky'),
		logoNode: <img src="https://images.nfctron.com/event-groups/7e5b5a4b-f4fe-4baf-8842-de445b5b0d9b.svg" className="h-14" alt="Marek Ztracený" />,
		getEventUrl(input) {
			return new URL(`https://vstupenky.marekztraceny.cz/koncert/${input.eventSlug}`);
		},
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.marekZtraceny[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('marek-ztraceny')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.marekZtraceny[0].value,
			organizerSlugs: ['marek-ztraceny'],
			eventGroupSlugs: ['marek-ztraceny-je-zpatky-tour-2025'],
		},
	},
	hradyCZ: {
		isMain: false,
		orgIntegratorId: 'hrady-cz-2025',
		name: 'HradyCZ',
		backUrl: new URL('https://hradycz.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="HradyCZ" />,
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.hradyCZ[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('hrady-cz')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.hradyCZ[0].value,
			organizerSlugs: ['riff-raff-sro'],
			eventGroupSlugs: ['hrady-cz-2025'],
		},
	},
	mklvFest: {
		isMain: false,
		orgIntegratorId: 'mklv-fest-2025',
		name: 'MKLV Fest',
		backUrl: new URL('https://mklvfest.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="MKLV Fest" />,
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.mklvFest[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('mklv-fest')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.mklvFest[0].value,
			organizerSlugs: ['art-delivery'],
			eventGroupSlugs: ['mklv-fest-2025'],
		},
	},
	zamosti: {
		isMain: false,
		orgIntegratorId: 'zamosti-2025',
		name: 'Zámostí',
		backUrl: new URL('https://zamosti.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="Zámostí" />,
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.zamosti[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('zamosti')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.zamosti[0].value,
			organizerSlugs: ['zamosti'],
			eventGroupSlugs: ['zamosti-2025'],
		},
	},
	pragueCarFestival: {
		isMain: false,
		orgIntegratorId: 'prague-car-festival-2025',
		name: 'Prague Car Festival',
		backUrl: new URL('https://praguecarfestival.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="Prague Car Festival" />,
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.pragueCarFestival[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('prague-car-festival')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.pragueCarFestival[0].value,
			organizerSlugs: ['prague-car-festival-sro'],
			eventGroupSlugs: ['prague-car-festival-2025'],
		},
	},
	pragueHarleyDays: {
		isMain: false,
		orgIntegratorId: 'prague-harley-days-2025',
		name: 'Prague Harley Days',
		backUrl: new URL('https://pragueharleydays.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="Prague Harley Days" />,
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.pragueHarleyDays[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('prague-harley-days')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.pragueHarleyDays[0].value,
			organizerSlugs: ['prague-harley-days-1649858729634'],
			eventGroupSlugs: ['prague-harley-days-2025'],
		},
	},
	pragueOpenAir: {
		isMain: false,
		orgIntegratorId: 'prague-open-air-2025',
		name: 'Prague Open Air',
		backUrl: new URL('https://pragueopenair.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="Prague Open Air" />,
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.pragueOpenAir[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('prague-open-air')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.pragueOpenAir[0].value,
			organizerSlugs: ['prague-open-air-sro'],
			eventGroupSlugs: ['prague-open-air'],
		},
	},
	soldout: {
		isMain: false,
		orgIntegratorId: 'soldout-festival-2025',
		name: 'Soldout Festival',
		backUrl: new URL('https://soldoutfestival.cz'),
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="Soldout Festival" />,
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.soldout[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('soldout-festival-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.soldout[0].value,
			organizerSlugs: ['event4life-sro'],
			eventGroupSlugs: ['soldout-festival-2025'],
		},
	},
	symphonika: {
		isMain: false,
		orgIntegratorId: 'symphonika-on-the-rock-2025',
		name: 'Symphonika On The Rock 2025',
		backUrl: new URL('https://www.symphonika.cz/'),
		logoNode: (
			<img src="https://images.nfctron.com/organizers/16446418-1008-42aa-8874-c92f2fd2c604.png" className="h-10" alt="Symphonika On The Rock 2025" />
		),
		matcher(url) {
			/** match custom DNS domain */
			if (url.hostname === orgIntegratorHosts.symphonika[0].value) return true;
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('symphonika-on-the-rock-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: {
			hostname: orgIntegratorHosts.symphonika[0].value,
			organizerSlugs: ['cbi-production'],
			eventGroupSlugs: ['symphonika-on-the-rock-2025'],
		},
	},
} as const satisfies Record<OrgIntegratorHost, OrgIntegratorConfig>;

// TODO: maybe somehow move / merge / generalize with misc.config.ts
export const REGISTERED_ORG_INTEGRATORS: OrgIntegratorConfig[] = [
	/**
	 * Random hacks
	 * TODO: check if this still needs to be here if we can config headerLogoUrl and backLink in event settings
	 */
	/** cirkus-cirkus-festival-2025 integrator */
	{
		orgIntegratorId: 'cirkus-cirkus-festival-2025',
		name: 'Cirkus Cirkus Festival 2025',
		isMain: false,
		backUrl: new URL('https://cirkusfestival.cz'),
		logoNode: (
			<img
				src="https://images.nfctron.com/event-groups/8f7f4ddf-3b07-4092-90a7-81ade4422ada.png"
				className="h-10"
				alt="Cirkus Cirkus Festival 2025"
			/>
		),
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('cirkus-cirkus-festival-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** 2025-spolek-zvonek integrator */
	{
		orgIntegratorId: '2025-spolek-zvonek',
		name: 'Spolek Zvonek 2025',
		isMain: false,
		backUrl: new URL('https://www.karpatyfest.cz'),
		logoNode: (
			<img src="https://images.nfctron.com/event-groups/b395b395-e18e-41f1-b972-5fa8265b9e09.svg" className="h-10" alt="Spolek Zvonek 2025" />
		),
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('2025-spolek-zvonek')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** prima-fresh-festival-2025 integrator */
	{
		orgIntegratorId: 'prima-fresh-festival-2025',
		name: 'Prima Fresh Festival 2025',
		isMain: false,
		backUrl: new URL('https://freshfestival.cz'),
		logoNode: (
			<img src="https://images.nfctron.com/event-groups/a6488505-ddd7-43c5-86bc-fe7a6c19de85.svg" className="h-10" alt="Prima Fresh Festival 2025" />
		),
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('prima-fresh-festival-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** vinarsky-spolek-moravska-nova-ves-2025 integrator */
	{
		orgIntegratorId: 'vinarsky-spolek-moravska-nova-ves-2025',
		name: 'Vinaršký Spolek Moravská Nová Ves 2025',
		isMain: false,
		backUrl: new URL('https://www.vinarimnves.cz'),
		logoNode: (
			<img
				src="https://images.nfctron.com/event-groups/564d57cb-177a-41bd-b29f-5909034763d7.png"
				className="h-[60px]"
				alt="Vinaršký Spolek Moravská Nová Ves 2025"
			/>
		),
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('vinarsky-spolek-moravska-nova-ves-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** eera-east-european-rock-alliance-2025 integrator */
	{
		orgIntegratorId: 'eera-east-european-rock-alliance-2025',
		name: 'EERA 2025',
		isMain: false,
		backUrl: new URL('https://www.facebook.com/EastEuropeanRockAlliance'),
		logoNode: <img src="https://images.nfctron.com/event-groups/5d777c4f-aea5-4ee6-b253-d7880138ef9e.svg" className="h-[70px]" alt="EERA 2025" />,
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('eera-east-european-rock-alliance-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** festivaly-a-udalosti-yashica-events-2025 integrator */
	{
		orgIntegratorId: 'festivaly-a-udalosti-yashica-events-2025',
		name: 'Yashica Events 2025',
		isMain: false,
		backUrl: new URL('https://www.yashica-events.cz'),
		logoNode: <img src="https://images.nfctron.com/event-groups/7f83f71c-7262-4163-9cfb-1363c6522e9f.png" className="h-10" alt="Yashica Events" />,
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('festivaly-a-udalosti-yashica-events-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** roxy-club-trebic-2025 integrator */
	{
		orgIntegratorId: 'roxy-club-trebic-2025',
		name: 'Roxy Club Třebíč 2025',
		isMain: false,
		backUrl: new URL('https://www.roxy-club.cz'),
		logoNode: (
			<img src="https://images.nfctron.com/event-groups/6dcc3b31-3c07-4aa5-b05e-928bfa3161cd.svg" className="h-10 w-20" alt="Roxy Club Třebíč" />
		),
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('roxy-club-trebic-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** pulsation-2025 integrator */
	{
		orgIntegratorId: 'pulsation-2025',
		name: 'Pulsation 2025',
		isMain: false,
		backUrl: new URL('https://linktr.ee/pulsation'),
		logoNode: <img src="https://images.nfctron.com/event-groups/75096011-ea5e-4fda-b113-854e1a1318c2.svg" className="h-10" alt="Pulsation" />,
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('pulsation-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},
	/** d-club-jihlava-2025 integrator */
	{
		orgIntegratorId: 'd-club-jihlava-2025',
		name: 'D-Club Jihlava 2025',
		isMain: false,
		backUrl: new URL('https://www.dclub.cz'),
		logoNode: <img src="https://images.nfctron.com/event-groups/2cd53cd6-a522-48cc-961f-cb3d23f7d268.png" className="h-10" alt="D-Club Jihlava" />,
		matcher(url) {
			/** match internal tickets.nfctron.com org */
			if (url.pathname.includes('d-club-jihlava-2025')) return true;
			return false;
		},
		reqSource: REQ_SOURCE.WEB,
		dnsConfig: null,
	},

	/**
	 * DNS Hostname / Integrators
	 */
	/** universal music / united tickets integrator */
	ORG_INTEGRATORS_DNS_HOSTS.unitedTickets,
	/** mirai-summer-fest-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.miraiFest,
	/** chinaski-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.chinaski,
	/** marek-ztraceny-je-zpatky-tour-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.marekZtraceny,
	/** hrady-cz-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.hradyCZ,
	/** mklv-fest-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.mklvFest,
	/** zamosti-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.zamosti,
	/** prague-car-festival-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.pragueCarFestival,
	/** prague-harley-days-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.pragueHarleyDays,
	/** prague-open-air-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.pragueOpenAir,
	/** soldout-festival-2025 integrator */
	ORG_INTEGRATORS_DNS_HOSTS.soldout,
	/** lovestream-tickets-events integrator */
	ORG_INTEGRATORS_DNS_HOSTS.lovestreamTicketsEvents,
	/** lovestream-tickets integrator */
	ORG_INTEGRATORS_DNS_HOSTS.lovestreamTickets,
	/** lovestream-vstupenky integrator */
	ORG_INTEGRATORS_DNS_HOSTS.lovestreamVstupenky,
	/** DINO LIVE integrator */
	ORG_INTEGRATORS_DNS_HOSTS.dinoLive,
	/* SYMPHONIKA ON THE ROCK 2025 */
	ORG_INTEGRATORS_DNS_HOSTS.symphonika,
	/** deafult (NFCtron) integrator */
	DEFAULT_ORG_INTEGRATOR,
] as const;

export const getOrgIntegratorConfig = (
	currentLocation: {
		hostname: string;
		pathname: string;
		origin: string;
	} | null,
): OrgIntegratorConfigReturn => {
	const match = REGISTERED_ORG_INTEGRATORS.find((orgIntegrator) => {
		if (!isDefined(currentLocation)) return false;
		return orgIntegrator.matcher(currentLocation);
	});

	/** fallback to nfctron default integrator */
	const configToReturn = match ?? DEFAULT_ORG_INTEGRATOR;
	return {
		/** default get event url */
		getEventUrl(input) {
			const origin = currentLocation?.origin ?? 'https://tickets.nfctron.com';
			const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
			return new URL(`${localePath}/event/${input.organizerSlug}/${input.eventSlug}`, origin);
		},
		/** default get event group url */
		getEventGroupUrl(input) {
			const origin = currentLocation?.origin ?? 'https://tickets.nfctron.com';
			const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
			return new URL(`${localePath}/tour/${input.eventGroupSlug}`, origin);
		},
		/** default get organizer url */
		getOrganizerUrl(input) {
			const origin = currentLocation?.origin ?? 'https://tickets.nfctron.com';
			const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
			return new URL(`${localePath}/organizer/${input.organizerSlug}`, origin);
		},
		/** current orgIntegrator config */
		...configToReturn,
		/** adjust for local dev */
		...(IS_LOCAL && {
			termsUrl: undefined,
			backUrl: new URL('http://localhost:4000'),
			getEventUrl(input) {
				const origin = currentLocation?.origin ?? 'https://tickets.nfctron.com';
				const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
				return new URL(`${localePath}/event/${input.organizerSlug}/${input.eventSlug}`, origin);
			},
			getEventGroupUrl(input) {
				const origin = currentLocation?.origin ?? 'https://tickets.nfctron.com';
				const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
				return new URL(`${localePath}/tour/${input.eventGroupSlug}`, origin);
			},
			getOrganizerUrl(input) {
				const origin = currentLocation?.origin ?? 'https://tickets.nfctron.com';
				const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
				return new URL(`${localePath}/organizer/${input.organizerSlug}`, origin);
			},
		}),
	};
};
